/**
 * Open one user-defined row's definition for editing, from the row itself.
 *
 * One step, unlike its neighbour: opening a dialog is reversible — Cancel
 * costs nothing — where the delete beside it is not, which is why only that
 * one arms a confirm.
 *
 * Takes the row key and hands it back, so the grid passes ONE callback for
 * every row rather than a closure per row, which would defeat the memo.
 */

import { Pencil } from "lucide-react";
import React, { useCallback } from "react";

export interface KpiCustomMetricEditProps {
	metricKey: string;
	/** What is being edited — named in the control's accessible label. */
	metricLabel: string;
	onEdit: (metricKey: string) => void;
}

const KpiCustomMetricEdit: React.FC<KpiCustomMetricEditProps> = ({
	metricKey,
	metricLabel,
	onEdit,
}) => {
	const handleClick = useCallback(() => onEdit(metricKey), [metricKey, onEdit]);

	return (
		<button
			type="button"
			onClick={handleClick}
			aria-label={`Edit ${metricLabel}`}
			// Revealed on row hover like the delete, and reachable from the
			// keyboard regardless: an opacity-only reveal would hide a control
			// focus can still land on.
			className="ml-2 rounded p-0.5 align-middle text-text-muted opacity-0 transition-opacity hover:bg-gray-200 focus:opacity-100 group-hover:opacity-100"
		>
			<Pencil className="h-3 w-3" />
		</button>
	);
};

export default React.memo(KpiCustomMetricEdit);
